import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { positionableUtil } from '@stormwater-software/common';
import classNames from 'classnames';
import moment from 'moment';
import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { RolesContext } from '../../../contexts/RolesContext';
import { dateFormat } from '../../../utils';
import findingUtil from '../../../utils/findings';
import InspectionContext from './../../../contexts/InspectionContext';
import ProjectContext from './../../../contexts/ProjectContext';
import { useClient } from '../../../hooks/client';
import FindingCheckbox from './FindingCheckbox';
import FindingImg from './FindingImg';
import TenantFeatureContext from '../../../contexts/TenantContext';
import AppContext from '../../../contexts/AppContext';

export const makeGoToDetailsAction =
  ({ history, isPublic, isGuest }) =>
    (inspectionId, findingId, project_uuid) => {
      history.push(isPublic ? `/public/inspection/${inspectionId}/findings/${findingId}/details` : (isGuest) ? `/guest/${project_uuid}/inspection/${inspectionId}/finding-details/${findingId}` : `/inspection/${inspectionId}/findings/${findingId}/details`);
    };

const getColor = (finding) => {
  let color = finding.color;

  if (!color) {
    if (
      finding.finding_type &&
      finding.finding_type.details &&
      finding.finding_type.details[0].color
    ) {
      color = finding.finding_type.details[0].color;
    } else {
      color = '#FF7272';
    }
  }

  return color;
};

export default function FindingCard({ finding }) {
  const {
    selectedFindings,
    readOnlyFindings,
    updateSelectedFindings,
    inspection,
  } = useContext(InspectionContext);

  const projectContext = useContext(ProjectContext);
  const tenantFeatureContext = useContext(TenantFeatureContext);
  const clientQuery = useClient(projectContext.project.client_id);
  const findingUploads = finding.finding_uploads;
  const history = useHistory();
  const isPublic = history.location.pathname.startsWith("/public")
  const { userHasPermission } = useContext(RolesContext);

  const appStore = useContext(AppContext);
  const user = appStore.get("user");
  const isGuest = history.location.pathname.startsWith("/guest") && user?.roleName === "Guest";

  const goToDetails = makeGoToDetailsAction({ history, isPublic, isGuest });
  const actionCheck = finding.createdNow ? false : finding.action_note ? finding.action_note.is_signed : true

  const canReadImages = userHasPermission('read', 'Finding Images');
  const dateCompleted = finding.date_completed
    ? moment.utc(finding.date_completed).format(dateFormat)
    : null;

  return (
    <div
      className={classNames('finding-wrapper pure-u pure-u-1 pure-u-md-1-2', {
        'finding-complete': finding.date_completed !== null,
      })}
      key={finding.id}
    >
      <div className='finding'>
        <div className='finding-content'>
          <div
            className='finding-text-info'
            onClick={() => {
              goToDetails(
                inspection.id ? inspection.id : finding.initial_inspection_id,
                finding.id,
                projectContext.guestProjectUUID
              );
            }}
          >
            <h4>
              {!readOnlyFindings && !finding.date_completed && !isPublic && actionCheck && (
                <FindingCheckbox
                  checked={selectedFindings.includes(finding.id)}
                  handleClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    updateSelectedFindings(finding.id);
                  }}
                />
              )}
              <span
                className='tag pad-right'
                style={{
                  backgroundColor:
                    finding.type === 'MI' ||
                      finding.type === 'A' ||
                      finding.type === 'CA' ||
                      finding.color === null
                      ? findingUtil.getBGColor(finding.type, null)
                      : getColor(finding),
                }}
              >
                #{finding.number}
              </span>

              <span>
                {finding.type === 'CA'
                  ? 'Corrective Action'
                  : finding.type === 'A'
                    ? 'Achievement'
                    : finding.type === 'MI'
                      ? 'Maintenance Item'
                      : finding.type}
              </span>
              {finding.date_completed ? (
                <FontAwesomeIcon
                  icon='check'
                  className='finding-card-checkmark'
                />
              ) : null}
            </h4>

            {finding && finding.inspection_template_type &&
              <div className='finding-inspection-type'>
                Template Type | {finding.inspection_template_type.name}
              </div>
            }

            {inspection && inspection.inspection_template && inspection.inspection_template.inspectionTemplateType && (
              <div className='finding-inspection-type'>
                Template Type | {inspection.inspection_template.inspectionTemplateType.name}
              </div>
            )}

            <div className='finding-content-comments'>
              {positionableUtil.findings.getCardLabel(finding).map((item) => (
                <p key={item}>{item}</p>
              ))}
            </div>

            {finding.InspectionFindingModel &&
              finding.InspectionFindingModel.carried_over && userHasPermission('read', 'Carry Over') ? (
              <span className='carry-over'>
                Carry Over <i className='fa fa-exclamation-circle' />
              </span>
            ) : null}

            <div className='start-end-dates'>
              <div>
                Initiated:
                <strong>
                  {moment.utc(finding.date_initiated).format(dateFormat)}
                </strong>
              </div>

              {finding.date_completed ? (
                <div>
                  Completed:
                  <strong>{dateCompleted}</strong>
                </div>
              ) : null}
            </div>
            {
              finding.finding_action &&
              <div className='start-end-dates'>
                <div>
                  Action Plan:
                  <strong style={{ color: finding.action_note?.is_signed ? '#8dc058' : 'red' }}>
                    {finding.action_note?.is_signed ? "Approved" : "Not Approved"}
                  </strong>
                </div>
              </div>
            }
          </div>
          {clientQuery &&
            clientQuery.data &&
            !clientQuery.data.disable_images &&
            tenantFeatureContext.tenantHasFeature('Enable Photo Uploads') &&
            canReadImages &&
            findingUploads &&
            !isPublic &&
            findingUploads.length ? (
            <FindingImg
              sources={findingUploads.map((fndUpl) => {
                return {
                  guid: fndUpl.upload.GUID,
                  annotations: fndUpl.annotations,
                };
              })}
              lightbox
              imageNumber={findingUploads.length}
              title={`Finding #${finding.number}`}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
